/**
 * Format the "Injury Site" field on a consultation letter header.
 * Mirrors backend/utils/injurySiteFormatter.js — keep the two in sync.
 *
 * Combines the referral's injuryType (an uppercase enum like 'SHOULDER',
 * 'LUMBAR_SPINE') with the side from the patient questionnaire
 * (Form.clinicalData.injuredSide → 'Left' | 'Right' | 'Both').
 *
 * Spine and head injuries are midline — no side is shown for those.
 */

const INJURY_TYPE_LABELS = {
  KNEE: 'Knee',
  SHOULDER: 'Shoulder',
  ANKLE: 'Ankle',
  LUMBAR_SPINE: 'Lumbar Spine',
  CERVICAL_SPINE: 'Cervical Spine',
  THORACIC_SPINE: 'Thoracic Spine',
  HIP: 'Hip',
  FOOT: 'Foot',
  ACHILLES: 'Achilles Tendon',
  HAMSTRING: 'Hamstring',
  ELBOW: 'Elbow',
  WRIST: 'Wrist',
  HAND: 'Hand',
  RIB: 'Rib',
  PELVIS: 'Pelvis',
  THIGH: 'Thigh',
  TIBIA: 'Tibia',
  CONCUSSION: 'Concussion',
};

const NON_SIDED_TYPES = new Set([
  'LUMBAR_SPINE', 'CERVICAL_SPINE', 'THORACIC_SPINE', 'CONCUSSION',
]);

export function labelForInjuryType(injuryType) {
  if (!injuryType) return '';
  const key = String(injuryType).toUpperCase().replace(/\s+/g, '_');
  return INJURY_TYPE_LABELS[key] || injuryType;
}

function normalizeSide(side) {
  if (!side) return '';
  const s = String(side).trim().toLowerCase();
  if (s === 'left' || s === 'l') return 'Left';
  if (s === 'right' || s === 'r') return 'Right';
  if (s === 'both' || s === 'bilateral') return 'Bilateral';
  return '';
}

export function formatInjurySite(injuryType, injuredSide) {
  const label = labelForInjuryType(injuryType);
  if (!label) return '';
  const key = String(injuryType || '').toUpperCase().replace(/\s+/g, '_');
  if (NON_SIDED_TYPES.has(key)) return label;
  const side = normalizeSide(injuredSide);
  return side ? `${side} ${label}` : label;
}
